import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectCreateSchema, projectSchema, } from '../types/entities/Project.js';
import { useProjectsStore } from '../stores/useProjectsStore.js';
const projectsResponseSchema = z.array(projectSchema);
const createProjectResponseSchema = z.object({
    projectId: z.string(),
});
export const useGetProjects = (authenticatedRequest, autoFetch = true) => {
    const { setProjects } = useProjectsStore();
    return useQuery({
        queryKey: ['projects'],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: '/projects', method: 'GET' }, projectsResponseSchema);
            // Update the store when data is successfully fetched
            const projects = response?.data || [];
            setProjects(projects);
            return response;
        },
        enabled: autoFetch,
    });
};
export const useGetProject = (authenticatedRequest, projectId) => {
    return useQuery({
        queryKey: ['projects', projectId],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: `/projects/${projectId}`, method: 'GET' }, projectSchema);
            return response;
        },
        enabled: !!projectId,
    });
};
export const useCreateProject = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    const { addProject, removeProject } = useProjectsStore();
    return useMutation({
        mutationFn: (data) => {
            const validatedData = projectCreateSchema.parse(data);
            return authenticatedRequest({
                url: '/projects',
                method: 'POST',
                data: validatedData,
            }, createProjectResponseSchema);
        },
        onMutate: async (data) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['projects'],
            });
            // Optimistically add the project to the store
            const tempProject = {
                id: `temp-${Date.now()}`, // Temporary ID
                userId: 'temp-user', // It won't matter locally before real refetch
                ...data,
                lifeDomains: data.lifeDomains || [],
                labels: data.labels || [],
                color: data.color || '#000000',
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            addProject(tempProject);
            return { tempProject };
        },
        onSuccess: (response, variables, context) => {
            // Remove the temporary project and invalidate to get the real data
            if (context?.tempProject) {
                removeProject(context.tempProject.id);
            }
            // Invalidate queries to refetch with the actual server data
            queryClient.invalidateQueries({
                queryKey: ['projects'],
            });
        },
        onError: (error, variables, context) => {
            // Remove the optimistically added project on error
            if (context?.tempProject) {
                removeProject(context.tempProject.id);
            }
        },
    });
};
export const useUpdateProject = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    const { updateProject } = useProjectsStore();
    return useMutation({
        mutationFn: ({ projectId, data }) => {
            const validatedData = projectCreateSchema.partial().parse(data);
            return authenticatedRequest({
                url: `/projects/${projectId}`,
                method: 'PUT',
                data: validatedData,
            }, z.object({ message: z.string() }));
        },
        onMutate: async ({ projectId, data }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['projects'],
            });
            // Get the current projects data from the query cache
            const previousProjects = queryClient.getQueryData(['projects']);
            // Find the original project for potential rollback
            const originalProject = previousProjects?.data?.find((project) => project.id === projectId);
            if (originalProject) {
                // Create the optimistically updated project
                const optimisticProject = {
                    ...originalProject,
                    ...data,
                };
                // Optimistically update the project in the store
                updateProject(optimisticProject);
            }
            return { originalProject, previousProjects };
        },
        onSuccess: (response, variables, context) => {
            // Invalidate queries to ensure we have the latest server data
            queryClient.invalidateQueries({
                queryKey: ['projects'],
            });
        },
        onError: (error, variables, context) => {
            // Rollback: restore the original project if the update failed
            if (context?.originalProject) {
                updateProject(context.originalProject);
            }
            // Optionally restore the entire previous state
            if (context?.previousProjects) {
                queryClient.setQueryData(['projects'], context.previousProjects);
            }
        },
    });
};
export const useDeleteProject = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    const { removeProject, addProject } = useProjectsStore();
    return useMutation({
        mutationFn: (projectId) => {
            return authenticatedRequest({
                url: `/projects/${projectId}`,
                method: 'DELETE',
            }, z.object({
                message: z.string(),
            }));
        },
        onMutate: async (projectId) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['projects'],
            });
            // Get the current projects data from the query cache
            const previousProjects = queryClient.getQueryData(['projects']);
            // Find the project to be removed for potential rollback
            const projectToRemove = previousProjects?.data?.find((project) => project.id === projectId);
            // Optimistically remove the project from the store
            removeProject(projectId);
            return { projectToRemove, previousProjects };
        },
        onSuccess: (response, variables, context) => {
            // Invalidate queries to ensure we have the latest server data
            queryClient.invalidateQueries({
                queryKey: ['projects'],
            });
        },
        onError: (error, variables, context) => {
            // Rollback: re-add the project if the deletion failed
            if (context?.projectToRemove) {
                addProject(context.projectToRemove);
            }
            // Optionally restore the entire previous state
            if (context?.previousProjects) {
                queryClient.setQueryData(['projects'], context.previousProjects);
            }
        },
    });
};
